import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ScrollText, RotateCw } from "lucide-react";
import Breadcrumb from "@/components/Breadcrumb";

const EVENTS = [
  { value: "", label: "All events" },
  { value: "login_success", label: "Login — success" },
  { value: "login_failed", label: "Login — failed" },
  { value: "login_locked", label: "Login — locked out" },
  { value: "user_created", label: "User created" },
  { value: "user_role_changed", label: "Role changed" },
  { value: "user_enabled", label: "User enabled" },
  { value: "user_disabled", label: "User disabled" },
  { value: "user_unlocked", label: "User unlocked" },
  { value: "user_deleted", label: "User deleted" },
  { value: "password_changed", label: "Password changed" },
  { value: "photo_names_applied", label: "Photos renamed" },
];

const EVENT_COLOR = {
  login_success: "text-emerald-300 bg-emerald-950 border-emerald-800",
  login_failed: "text-amber-300 bg-amber-950 border-amber-800",
  login_locked: "text-red-300 bg-red-950 border-red-800",
  user_created: "text-sky-300 bg-sky-950 border-sky-800",
  user_role_changed: "text-violet-300 bg-violet-950 border-violet-800",
  user_disabled: "text-red-300 bg-red-950 border-red-800",
  user_enabled: "text-emerald-300 bg-emerald-950 border-emerald-800",
  user_unlocked: "text-amber-300 bg-amber-950 border-amber-800",
  user_deleted: "text-red-200 bg-red-950 border-red-700",
  password_changed: "text-sky-300 bg-sky-950 border-sky-800",
  photo_names_applied: "text-amber-300 bg-amber-950 border-amber-800",
};

function formatMeta(m) {
  if (!m || Object.keys(m).length === 0) return "";
  return Object.entries(m)
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
}

/**
 * Admin audit-log viewer. Backend endpoint supports filter by event,
 * actor_email, target_email, and a `since` ISO timestamp; we expose the
 * three most useful filters in the UI (event + email + since).
 */
export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [event, setEvent] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [since, setSince] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: "300" });
      if (event) params.set("event", event);
      if (emailFilter) params.set("target_email", emailFilter.trim().toLowerCase());
      if (since) params.set("since", new Date(since).toISOString());
      const { data } = await api.get(`/audit?${params.toString()}`);
      setRows(data || []);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Breadcrumb items={[{ label: "Admin", to: "/admin" }, { label: "Audit Log" }]} />
          <h1
            className="font-display font-black text-3xl sm:text-4xl tracking-tight"
            data-testid="audit-heading"
          >
            Audit Log
          </h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Event</Label>
            <Select value={event || "_all"} onValueChange={(v) => setEvent(v === "_all" ? "" : v)}>
              <SelectTrigger
                className="bg-slate-950 border-slate-700 mt-1 w-56"
                data-testid="audit-event-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENTS.map((e) => (
                  <SelectItem key={e.value || "_all"} value={e.value || "_all"}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Target email</Label>
            <Input
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="user@example.com"
              className="bg-slate-950 border-slate-700 mt-1 w-56 font-mono"
              data-testid="audit-email-filter"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Since</Label>
            <Input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="bg-slate-950 border-slate-700 mt-1 w-52"
              data-testid="audit-since-filter"
            />
          </div>
          <Button
            onClick={refresh} disabled={busy}
            className="bg-red-700 hover:bg-red-600 font-display tracking-wider"
            data-testid="audit-refresh"
          >
            <RotateCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />
            APPLY
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0 divide-y divide-slate-800" data-testid="audit-list">
          {rows.map((r) => (
            <div
              key={r.id}
              className="p-3 flex flex-wrap items-center gap-3 text-sm"
              data-testid={`audit-row-${r.id}`}
            >
              <span
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-bold whitespace-nowrap ${
                  EVENT_COLOR[r.event] || "text-slate-300 bg-slate-800 border-slate-700"
                }`}
              >
                {r.event.replaceAll("_", " ")}
              </span>
              <div className="flex-1 min-w-[200px]">
                <div className="text-slate-100">
                  <span className="font-mono text-xs text-slate-300">{r.actor_email || "—"}</span>
                  <span className="text-slate-500 mx-2">→</span>
                  <span className="font-mono text-xs text-slate-300">{r.target_email || "—"}</span>
                </div>
                {Object.keys(r.metadata || {}).length > 0 && (
                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                    {formatMeta(r.metadata)}
                  </div>
                )}
              </div>
              <div className="text-[11px] text-slate-400 font-mono whitespace-nowrap">
                {r.ip || "—"}
              </div>
              <div className="text-[11px] text-slate-400 font-mono whitespace-nowrap">
                {new Date(r.created_at).toLocaleString()}
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              <ScrollText className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No entries match the current filters.
            </div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
